/**
 * AlertTableView (Story 27.2) — a clone of the clients ClientTableView. Same
 * table chrome (rounded-[22px] bg-muted/50 shadow-xs, 0.5px borders, header
 * bg-background + 0.4-opacity divider, sortable headers, 3-dot DropdownMenu
 * with DotsThreeVertical size 28) per the design-system directive §7. Columns
 * swapped to the alert read-model.
 *
 * Column show/hide: every hideable column's <th>/<td> is gated by isHidden(id).
 * The Alert (name) + Actions columns are pinned. The "Columns" dropdown in the
 * toolbar owns visibility; the hidden-columns set is owned by the route and
 * persisted to localStorage. Column ids mirror ./alertColumns.
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  SortAscending,
  SortDescending,
  PencilSimple,
  Trash,
  Bell,
  PauseCircle,
} from "@phosphor-icons/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import type { Alert, SortOption } from "./types";
import {
  platformDisplayName,
  platformLogoUrl,
  humanizeMetricKey,
  readableCondition,
  lastTriggeredLabel,
} from "./alertDisplay";

interface AlertTableViewProps {
  alerts: Alert[];
  canManage: boolean;
  onAlertClick?: (alert: Alert) => void;
  onEditClick?: (alert: Alert) => void;
  onDeleteClick?: (alert: Alert) => void;
  onToggleActive?: (alert: Alert) => void;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

function StatusToggle({
  alert,
  canManage,
  onToggleActive,
}: {
  alert: Alert;
  canManage: boolean;
  onToggleActive?: (alert: Alert) => void;
}) {
  const label = alert.isActive ? "Active" : "Paused";
  // Match the goals status-pill design: a soft tinted pill + Phosphor icon,
  // sentence-case label, no border. Active = emerald (like a goal on-track),
  // Paused = muted gray (like a paused goal).
  const classes = alert.isActive
    ? "bg-emerald-100 text-emerald-700"
    : "bg-muted text-muted-foreground";
  const StatusIcon = alert.isActive ? Bell : PauseCircle;
  if (!canManage || !onToggleActive) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}
      >
        <StatusIcon size={12} weight="bold" />
        {label}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggleActive(alert);
      }}
      className={`touch-target-sm inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-sm ${classes}`}
      aria-label={`${alert.isActive ? "Pause" : "Resume"} ${alert.name}`}
    >
      <StatusIcon size={12} weight="bold" />
      {label}
    </button>
  );
}

export function AlertTableView({
  alerts,
  canManage,
  onAlertClick,
  onEditClick,
  onDeleteClick,
  onToggleActive,
  triggerRefs,
  sortBy,
  onSortChange,
  hiddenColumns = [],
}: AlertTableViewProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Alert list">
        <thead className="relative bg-background">
          <tr>
            {/* Alert — pinned (not hideable) */}
            <th
              scope="col"
              className="text-left py-2 px-4 text-[13px] font-semibold text-foreground"
            >
              {onSortChange ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() =>
                    onSortChange(sortBy === "name_asc" ? "name_desc" : "name_asc")
                  }
                >
                  <TableHeaderIcon name="alert" />
                  Alert
                  {(sortBy === "name_asc" || sortBy === "name_desc") &&
                    (sortBy === "name_asc" ? (
                      <SortAscending size={18} />
                    ) : (
                      <SortDescending size={18} />
                    ))}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name="alert" />
                  Alert
                </span>
              )}
            </th>

            {!isHidden("status") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="status" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "status_asc" ? "status_desc" : "status_asc",
                        )
                      }
                    >
                      Status
                      {(sortBy === "status_asc" || sortBy === "status_desc") &&
                        (sortBy === "status_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Status"
                  )}
                </span>
              </th>
            )}

            {!isHidden("condition") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="condition" />
                  Condition
                </span>
              </th>
            )}

            {!isHidden("client") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="client" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "client_asc" ? "client_desc" : "client_asc",
                        )
                      }
                    >
                      Client
                      {(sortBy === "client_asc" || sortBy === "client_desc") &&
                        (sortBy === "client_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Client"
                  )}
                </span>
              </th>
            )}

            {!isHidden("platform") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="platform" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "platform_asc"
                            ? "platform_desc"
                            : "platform_asc",
                        )
                      }
                    >
                      Platform
                      {(sortBy === "platform_asc" ||
                        sortBy === "platform_desc") &&
                        (sortBy === "platform_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Platform"
                  )}
                </span>
              </th>
            )}

            {!isHidden("interval") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="interval" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "interval_asc"
                            ? "interval_desc"
                            : "interval_asc",
                        )
                      }
                    >
                      Interval
                      {(sortBy === "interval_asc" ||
                        sortBy === "interval_desc") &&
                        (sortBy === "interval_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Interval"
                  )}
                </span>
              </th>
            )}

            {!isHidden("lastTriggered") && (
              <th
                scope="col"
                className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap"
              >
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="time" />
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() =>
                        onSortChange(
                          sortBy === "last_triggered_asc"
                            ? "last_triggered_desc"
                            : "last_triggered_asc",
                        )
                      }
                    >
                      Last triggered
                      {(sortBy === "last_triggered_asc" ||
                        sortBy === "last_triggered_desc") &&
                        (sortBy === "last_triggered_asc" ? (
                          <SortAscending size={18} />
                        ) : (
                          <SortDescending size={18} />
                        ))}
                    </button>
                  ) : (
                    "Last triggered"
                  )}
                </span>
              </th>
            )}

            {canManage && (
              <th scope="col" className="py-2 px-4 w-16">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {alerts.map((alert) => {
            const metricLabel = humanizeMetricKey(alert.metricKey);
            const logo = platformLogoUrl(alert.platform);
            return (
              <tr
                key={alert.id}
                className={`bg-background hover:bg-brand/[0.02] transition-colors${onAlertClick ? " cursor-pointer" : ""}`}
                onClick={() => onAlertClick?.(alert)}
                onKeyDown={(e) => {
                  if (onAlertClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onAlertClick(alert);
                  }
                }}
                tabIndex={onAlertClick ? 0 : undefined}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium truncate">{alert.name}</p>
                  </div>
                </td>

                {!isHidden("status") && (
                  <td className="py-3 px-4">
                    <StatusToggle
                      alert={alert}
                      canManage={canManage}
                      onToggleActive={onToggleActive}
                    />
                  </td>
                )}

                {!isHidden("condition") && (
                  <td className="py-3 px-4 text-foreground hidden lg:table-cell whitespace-nowrap">
                    {readableCondition(
                      metricLabel,
                      alert.condition,
                      alert.threshold,
                    )}
                  </td>
                )}

                {!isHidden("client") && (
                  <td className="py-3 px-4 text-foreground hidden md:table-cell whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      <ClientAvatar
                        name={alert.clientName}
                        logoUrl={null}
                        size="sm"
                      />
                      <span
                        className="max-w-[160px] truncate"
                        title={alert.clientName}
                      >
                        {alert.clientName}
                      </span>
                    </span>
                  </td>
                )}

                {!isHidden("platform") && (
                  <td className="py-3 px-4 hidden sm:table-cell whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      {logo ? (
                        <img
                          src={logo}
                          alt=""
                          className="w-5 h-5 rounded-sm shrink-0 object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <span className="w-5 h-5 rounded-sm bg-muted shrink-0" />
                      )}
                      <span className="text-foreground">
                        {platformDisplayName(alert.platform)}
                      </span>
                    </span>
                  </td>
                )}

                {!isHidden("interval") && (
                  <td className="py-3 px-4 text-foreground hidden sm:table-cell whitespace-nowrap">
                    {alert.interval.charAt(0).toUpperCase() +
                      alert.interval.slice(1)}
                  </td>
                )}

                {!isHidden("lastTriggered") && (
                  <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                    {lastTriggeredLabel(alert.lastTriggeredAt)}
                  </td>
                )}

                {canManage && (
                  <td
                    className="py-3 px-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          ref={(el) => {
                            triggerRefs.current[alert.id] = el;
                          }}
                          variant="ghost"
                          size="icon"
                          className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                          aria-label={`Actions for ${alert.name}`}
                        >
                          <DotsThreeVertical size={28} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="rounded-[23px] p-2 shadow-md"
                        style={{ border: "0.5px solid var(--hairline)" }}
                      >
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onEditClick?.(alert)}
                        >
                          <PencilSimple size={14} className="mr-1" />
                          Edit Alert
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                          onClick={() => onDeleteClick?.(alert)}
                        >
                          <Trash size={14} className="mr-1" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
